import { Controller, FormProvider, useForm } from 'react-hook-form';
import { CSSProperties, useCallback, useContext, useState } from 'react';
import { AccountContext } from '../AccountContext';
import { useRefreshAccountId, useUpdateAccountStatus } from '@app/sdk';
import { FormContainer, Select, SelectOption } from '@app/shared';
import DatePicker from 'react-datepicker';
import { formatDate } from '../../../utilities/FormatHelpers';

export function AccountStatusForm({
  accountStatuses,
  currentStatusID,
  chargeOffReasons,
  closureReasons,
}: {
  accountStatuses: SelectOption[];
  currentStatusID: string;
  chargeOffReasons: SelectOption[];
  closureReasons: SelectOption[];
}) {
  const flexRowSpaceBetween: CSSProperties = { display: 'flex', flexDirection: 'row', justifyContent: 'space-between' };

  const formContext = useForm();

  const [selectedStatus, setSelectedStatus] = useState<string>(currentStatusID);
  const { accountId } = useContext(AccountContext);
  const refreshAccount = useRefreshAccountId(accountId);
  const { mutateAsync: updateAccountStatus, isPending: isUpdatingAccountStatus } = useUpdateAccountStatus();
  const onAccountStatusUpdateClicked = useCallback(
    async (data: { name: string; reasonCode?: string, effectiveDate?: Date }) => {
      await updateAccountStatus({ accountId, newAccountStatus: data?.name, accountStatusReason: data?.reasonCode, effectiveDate: formatDate(data?.effectiveDate) });
      refreshAccount();
    },
    [accountId, refreshAccount, updateAccountStatus]
  );

  const reasonRequired = () => (selectedStatus === 'ChargeOff' || selectedStatus === 'AccountClosedPayDown' ? 'Reason Code is required' : false);
  return (
    <FormProvider {...formContext}>
      <FormContainer isSubmitting={isUpdatingAccountStatus} onSubmit={onAccountStatusUpdateClicked}>
        <Select
          key={currentStatusID}
          defaultValue={selectedStatus}
          name={'name'}
          onChange={(ev) => {
            setSelectedStatus(ev.target.value);
            if (ev.target.value !== 'ChargeOff' && ev.target.value !== 'AccountClosedPayDown') {
              formContext.setValue('reasonCode', null);
            }
          }}
          options={{ required: 'Status is required' }}
          label={'Status'}
          selectOptions={accountStatuses}
        />
        {selectedStatus === 'ChargeOff' && (
          <>
          <Select
            selectOptions={chargeOffReasons}
            options={{ required: reasonRequired() }}
            name={'reasonCode'}
            label={'Reason Code'}
            defaultValue={''}
          />
          <Controller
            name="effectiveDate"
            control={formContext.control}
            render={({ field }) => (
              <div className="sixteen wide column" style={flexRowSpaceBetween}>
                <label className="ui label extra large datepicker-label">Effective date</label>
                <DatePicker
                  name="effectiveDate"
                  selected={field.value}
                  onChange={(date) => {
                    field.onChange(date);
                  }}
                  dateFormat="dd/MM/yyyy"
                  placeholderText="Effective date"
                  isClearable
                />
              </div>
            )}
          />
          </>
        )}
        {selectedStatus === 'AccountClosedPayDown' && (
            <Select
                selectOptions={closureReasons}
                options={{ required: reasonRequired() }}
                name={'reasonCode'}
                label={'Reason Code'}
                defaultValue={''}
            />
        )}
      </FormContainer>
    </FormProvider>
  );
}
